import { bindings, capturing, turboKey } from "./keymap";
import { EJS_VERSION } from "./core-version";

// N64 runs on EmulatorJS (mupen64plus_next), vendored under public/ejs/ by
// scripts/update-core.mjs. It can't be torn down once started, so a page
// runs at most one of its games and leaving it is a full page load.
export const EJS_PLAYER_ID = "ejs-player";

export type EjsSystem = "n64";

export const EJS_SYSTEM_LABEL: Record<EjsSystem, string> = {
  n64: "Nintendo 64",
};

const EXTENSIONS: Record<EjsSystem, string[]> = {
  n64: ["z64", "n64", "v64"],
};

// PlayStation and PSP were dropped on 2026-10-22. Their games can still be in
// a browser's store or the cloud library; these spot them so they are hidden
// instead of being taken for N64 or Game Boy Advance games.
const RETIRED_SYSTEMS = new Set(["psx", "psp"]);
const RETIRED_EXTENSIONS = new Set(["cue", "bin", "chd", "pbp", "m3u", "img", "iso", "cso"]);

const DATA_PATH = `/ejs/${EJS_VERSION}/`;
// Named for when this held only N64 ROMs; kept so stored games survive.
const DB_NAME = "pocket-play-n64";
const STORE = "roms";

// Every key is fed in by hand through simulateInput, keyed by
// KeyboardEvent.code. EmulatorJS's own keyboard bindings are cleared: it
// takes one key per control, and setting them through its controls object
// did not reliably take effect. The keys are the shared layout's
// (keymap.json); the ids are EmulatorJS's N64 controls: 0 A, 1 B, 3 Start,
// 4-7 D-pad up/down/left/right, 10/11 L/R, 12 Z, 16-19 stick
// right/left/down/up, 20-23 C buttons. Movement is the analog stick; the
// D-pad has keys of its own.
const N64_CONTROL: Record<string, number> = {
  A: 0,
  B: 1,
  Start: 3,
  "D-pad up": 4,
  "D-pad down": 5,
  "D-pad left": 6,
  "D-pad right": 7,
  L: 10,
  R: 11,
  "Z trigger": 12,
  Right: 16,
  Left: 17,
  Down: 18,
  Up: 19,
  "C right": 20,
  "C left": 21,
  "C down": 22,
  "C up": 23,
};
const keyControls = (system: EjsSystem): Record<string, number> =>
  Object.fromEntries(bindings(system).flatMap(({ button, keys }) => keys.map((code) => [code, N64_CONTROL[button]])));
// Analog stick directions take a full-deflection value instead of 1.
const STICK: Record<EjsSystem, Set<number>> = {
  n64: new Set([16, 17, 18, 19]),
};
const STICK_FULL = 0x7fff;

// EmulatorJS's own N64 touch layout, with Z beside L instead of on top of
// the D-pad and no Fast/Slow buttons (the page's turbo does that). Our own
// buttons keep to the top centre (see .ejs-game in the CSS), clear of it.
type TouchControl = Record<string, string | number | boolean | number[]>;
const DPAD = (top: string): TouchControl => ({
  type: "zone",
  id: "dpad",
  location: "left",
  left: "50%",
  top,
  joystickInput: false,
  inputValues: [4, 5, 6, 7],
});
const shoulder = (text: string, side: "left" | "right", top: number, input: number): TouchControl => ({
  type: "button",
  text,
  id: text.toLowerCase(),
  block: true,
  location: "top",
  [side]: 10,
  top,
  bold: true,
  input_value: input,
});
const TOUCH_LAYOUT: Record<EjsSystem, TouchControl[]> = {
  n64: [
    { type: "button", text: "B", id: "b", location: "right", left: -10, top: 95, input_value: 1, bold: true },
    { type: "button", text: "A", id: "a", location: "right", left: 40, top: 150, input_value: 0, bold: true },
    { type: "zone", id: "stick", location: "left", left: "50%", top: "100%", joystickInput: true, inputValues: [16, 17, 18, 19] },
    DPAD("0%"),
    { type: "button", text: "Start", id: "start", location: "right", left: 80, top: 100, fontSize: 15, block: true, input_value: 3 },
    shoulder("L", "left", -40, 10),
    shoulder("R", "right", -40, 11),
    { type: "button", text: "Z", id: "z", block: true, location: "top", left: 70, top: -40, bold: true, input_value: 12 },
    { fontSize: 20, type: "button", text: "CU", id: "cu", joystickInput: true, location: "right", left: 25, top: -65, input_value: 23 },
    { fontSize: 20, type: "button", text: "CD", id: "cd", joystickInput: true, location: "right", left: 25, top: 15, input_value: 22 },
    { fontSize: 20, type: "button", text: "CL", id: "cl", joystickInput: true, location: "right", left: -15, top: -25, input_value: 21 },
    { fontSize: 20, type: "button", text: "CR", id: "cr", joystickInput: true, location: "right", left: 65, top: -25, input_value: 20 },
  ],
};

interface EjsEmulator {
  started: boolean;
  controls: Record<number, Record<number, { value?: string | number }>>;
  setupKeys(): void;
  checkGamepadInputs(): void;
  pause(dontUpdate?: boolean): void;
  play(dontUpdate?: boolean): void;
  setVolume(volume: number): void;
  on(event: string, callback: () => void): void;
  gameManager?: {
    saveSaveFiles(): void;
    /** The running game's state; throws if the core can't serialize yet. */
    getState(): Uint8Array;
    loadState(state: Uint8Array): void;
    /** Resets the console. */
    restart(): void;
    toggleFastForward(on: 0 | 1): void;
    /** 0 is unlimited. */
    setFastForwardRatio(ratio: number): void;
    simulateInput(player: number, index: number, value: number): void;
    /** Flushes the game save to its file and returns it, or null. */
    getSaveFile(save?: boolean): Uint8Array | null;
    getSaveFilePath(): string;
    /** Makes the core re-read its save files. */
    loadSaveFiles(): void;
    FS: {
      analyzePath(path: string): { exists: boolean };
      mkdir(path: string): void;
      unlink(path: string): void;
      writeFile(path: string, data: Uint8Array): void;
    };
  };
  menu?: { open(force?: boolean): void; close(): void };
}

export interface EjsSession {
  system: EjsSystem;
  pause(): void;
  resume(): void;
  setMuted(muted: boolean): void;
  flushSave(): void;
  /** Shows or hides EmulatorJS's own menu bar. */
  setMenu(open: boolean): void;
  /** The game save (N64 cartridge memory), or null before the game writes one. */
  getSave(): Uint8Array | null;
  /** Replaces the game save; the running game picks it up. */
  putSave(bytes: Uint8Array): void;
  /** A save state of the running game, or null if the core can't make one yet. */
  getState(): Uint8Array | null;
  loadState(bytes: Uint8Array): void;
  /** Runs at this multiple of normal speed; 1 is normal. */
  setSpeed(multiplier: number): void;
  /** Resets the console, as its reset button would. */
  reset(): void;
  /** Presses an EmulatorJS control: 0/1 for a button, up to 0x7fff for a stick direction. */
  input(index: number, value: number): void;
}

export function ejsSystemOfName(name: string): EjsSystem | null {
  const ext = name.slice(name.lastIndexOf(".") + 1).toLowerCase();
  for (const system of Object.keys(EXTENSIONS) as EjsSystem[]) {
    if (EXTENSIONS[system].includes(ext)) return system;
  }
  return null;
}

const u16 = (b: Uint8Array, at: number) => b[at] | (b[at + 1] << 8);
const u32 = (b: Uint8Array, at: number) => (u16(b, at) | (u16(b, at + 2) << 16)) >>> 0;

/**
 * Every file name in a zip, from its central directory (the table of contents
 * at the end), so folder entries or a readme first don't hide the game. Falls
 * back to the first local file header when there is no directory to read.
 */
export async function zipEntryNames(file: Blob): Promise<string[]> {
  const tailSize = Math.min(file.size, 22 + 0xffff);
  const tail = new Uint8Array(await file.slice(file.size - tailSize).arrayBuffer());
  for (let at = tail.length - 22; at >= 0; at--) {
    if (u32(tail, at) !== 0x06054b50) continue;
    const size = u32(tail, at + 12);
    const offset = u32(tail, at + 16);
    const dir = new Uint8Array(await file.slice(offset, offset + size).arrayBuffer());
    const names: string[] = [];
    for (let p = 0; p + 46 <= dir.length && u32(dir, p) === 0x02014b50; ) {
      const nameLength = u16(dir, p + 28);
      names.push(new TextDecoder().decode(dir.subarray(p + 46, p + 46 + nameLength)));
      p += 46 + nameLength + u16(dir, p + 30) + u16(dir, p + 32);
    }
    if (names.length) return names;
    break;
  }
  const head = new Uint8Array(await file.slice(0, 30 + 512).arrayBuffer());
  if (u32(head, 0) !== 0x04034b50) return [];
  return [new TextDecoder().decode(head.subarray(30, 30 + u16(head, 26)))];
}

/** What a zip holds, or null if no EmulatorJS game is in it. */
async function ejsSystemOfZip(file: Blob): Promise<EjsSystem | null> {
  for (const name of await zipEntryNames(file)) {
    const system = ejsSystemOfName(name);
    if (system) return system;
  }
  return null;
}

const extensionOf = (name: string) => name.slice(name.lastIndexOf(".") + 1).toLowerCase();

export const retiredMessage = (name: string) =>
  `${name} is a PlayStation or PSP game. CloudBoy plays Game Boy, Game Boy Color, Game Boy Advance and N64 games.`;

/**
 * Whether a game is for a console CloudBoy no longer runs (PlayStation, PSP):
 * remembered as one, named like one, or a zip holding one and no game it can
 * play.
 */
export async function isRetiredGame(name: string, file: Blob | null): Promise<boolean> {
  try {
    if (RETIRED_SYSTEMS.has(localStorage.getItem(systemKey(name)) ?? "")) return true;
  } catch {
    // Storage can be blocked; go by the file.
  }
  if (RETIRED_EXTENSIONS.has(extensionOf(name))) return true;
  if (!file || extensionOf(name) !== "zip") return false;
  const entries = (await zipEntryNames(file)).map(extensionOf);
  return entries.some((ext) => RETIRED_EXTENSIONS.has(ext)) && !entries.some((ext) => ["gb", "gbc", "gba", ...EXTENSIONS.n64].includes(ext));
}

// A .7z or .rar can't be looked inside here, so when CloudBoy already knows
// a game's system (a cloud row picked from the computer) it is remembered by
// file name and wins over guessing.
const systemKey = (name: string) => `pocket-play.ejsSystem.${name}`;

// Storage can be blocked (private windows); then CloudBoy just guesses.
export function rememberSystem(name: string, system: EjsSystem) {
  try {
    localStorage.setItem(systemKey(name), system);
  } catch {
    // Ignore storage errors
  }
}

export function rememberedSystem(name: string): EjsSystem | null {
  try {
    const system = localStorage.getItem(systemKey(name));
    return system && system in EJS_SYSTEM_LABEL ? (system as EjsSystem) : null;
  } catch {
    return null;
  }
}

export function isEjsSystem(system: string): system is EjsSystem {
  return system in EJS_SYSTEM_LABEL;
}

/** The EmulatorJS system for a game file, reading a zip's contents when needed; null for mGBA games. */
export async function ejsSystemOf(name: string, file: Blob): Promise<EjsSystem | null> {
  return (
    rememberedSystem(name) ??
    (name.toLowerCase().endsWith(".zip") ? ejsSystemOfZip(file) : ejsSystemOfName(name))
  );
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = run(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
}

// Games are kept as bytes: WebKit refuses to store some Blobs (a File from
// a folder pick, for one). Older entries are Blobs, read back as they are.
export const ejsRoms = {
  list: async () => ((await withStore("readonly", (s) => s.getAllKeys())) as string[]).sort(),
  get: async (name: string): Promise<Blob | undefined> => {
    const stored = await withStore<Blob | ArrayBuffer | undefined>("readonly", (s) => s.get(name));
    return stored instanceof ArrayBuffer ? new Blob([stored]) : stored;
  },
  put: async (name: string, blob: Blob) => {
    const bytes = await blob.arrayBuffer();
    await withStore("readwrite", (s) => s.put(bytes, name));
  },
  remove: (name: string) => withStore("readwrite", (s) => s.delete(name)),
  /** The system of a stored game; N64 if it can't be told (the only one there used to be). */
  system: async (name: string): Promise<EjsSystem> => {
    const blob = await ejsRoms.get(name);
    return (blob && (await ejsSystemOf(name, blob))) ?? "n64";
  },
};

/** EmulatorJS's own progress line while it starts ("Download Game Data 45%"), if any. */
export function ejsProgress(): string | null {
  const ejs = (window as unknown as { EJS_emulator?: { textElem?: HTMLElement } }).EJS_emulator;
  return ejs?.textElem?.textContent?.trim() || null;
}

let session: Promise<EjsSession> | null = null;

/** `onTurbo` hears Space held and released, the same turbo key mGBA games have. */
export function startEjs(
  name: string,
  rom: Blob,
  system: EjsSystem,
  muted: boolean,
  onTurbo: (down: boolean) => void,
): Promise<EjsSession> {
  if (session) return session;
  session = new Promise<EjsSession>((resolve, reject) => {
    const w = window as unknown as Record<string, unknown>;
    w.EJS_player = `#${EJS_PLAYER_ID}`;
    w.EJS_core = system;
    w.EJS_VirtualGamepadSettings = TOUCH_LAYOUT[system];
    w.EJS_pathtodata = DATA_PATH;
    w.EJS_gameUrl = URL.createObjectURL(rom);
    // Names the game inside the core's filesystem and keys its saves and states.
    w.EJS_gameName = name;
    w.EJS_language = "en-US";
    w.EJS_startOnLoaded = true;
    w.EJS_threads = window.crossOriginIsolated;
    w.EJS_volume = muted ? 0 : 0.5;
    w.EJS_color = "#7c6cf0";
    w.EJS_ready = () => {};
    w.EJS_onGameStart = () => {
      const ejs = w.EJS_emulator as EjsEmulator;
      // Clear EmulatorJS's keyboard bindings (gamepad ones stay) so its
      // defaults (Tab, V, A, R...) don't fire alongside ours.
      for (const control of Object.values(ejs.controls[0])) delete control.value;
      ejs.setupKeys();
      ejs.checkGamepadInputs();
      const stick = STICK[system];
      const onKey = (e: KeyboardEvent) => {
        if (capturing) return;
        if (e.code === turboKey() && !e.metaKey && !e.ctrlKey && !e.altKey && !(e.target as Element | null)?.matches?.("input, select, textarea")) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (!e.repeat) onTurbo(e.type === "keydown");
          return;
        }
        const index = keyControls(system)[e.code];
        if (index === undefined || e.metaKey || e.ctrlKey || e.altKey) return;
        if ((e.target as Element | null)?.matches?.("input, select, textarea")) return;
        e.preventDefault();
        // Stops EmulatorJS's own listener reacting to the same key.
        e.stopImmediatePropagation();
        if (e.repeat) return;
        const down = e.type === "keydown";
        ejs.gameManager?.simulateInput(0, index, down ? (stick.has(index) ? STICK_FULL : 1) : 0);
      };
      window.addEventListener("keydown", onKey, true);
      window.addEventListener("keyup", onKey, true);
      // The touch sticks (nipplejs, static mode) measure their centre when
      // created and again only on a window resize, so one built before the
      // player settled answers touches as if from somewhere else.
      // dynamicPage re-measures it on every move; set just before nipplejs
      // sees the touch, it covers sticks EmulatorJS rebuilds later too.
      document.getElementById(EJS_PLAYER_ID)?.addEventListener(
        "pointerdown",
        () => {
          // nipplejs's factory is the list of its sticks.
          const nipplejs = w.nipplejs as { factory?: { options: { dynamicPage?: boolean } }[] } | undefined;
          for (const sticks of nipplejs?.factory ?? []) sticks.options.dynamicPage = true;
        },
        true,
      );
      resolve({
        system,
        pause: () => ejs.pause(),
        resume: () => ejs.play(),
        setMuted: (m) => ejs.setVolume(m ? 0 : 0.5),
        flushSave: () => ejs.gameManager?.saveSaveFiles(),
        // force: no auto-hide timer while we hold it open.
        setMenu: (open) => (open ? ejs.menu?.open(true) : ejs.menu?.close()),
        getSave: () => ejs.gameManager?.getSaveFile() ?? null,
        // As EmulatorJS's own "load save file" does: write the file in
        // place, then have the core re-read it. /data/saves is an
        // autoPersist IDBFS mount, so it reaches IndexedDB on its own.
        putSave: (bytes) => {
          const gm = ejs.gameManager;
          if (!gm) return;
          const path = gm.getSaveFilePath();
          let dir = "";
          for (const part of path.split("/").slice(1, -1)) {
            dir += `/${part}`;
            if (!gm.FS.analyzePath(dir).exists) gm.FS.mkdir(dir);
          }
          if (gm.FS.analyzePath(path).exists) gm.FS.unlink(path);
          gm.FS.writeFile(path, bytes);
          gm.loadSaveFiles();
        },
        getState: () => {
          try {
            return ejs.gameManager?.getState() ?? null;
          } catch {
            return null;
          }
        },
        loadState: (bytes) => ejs.gameManager?.loadState(bytes),
        reset: () => ejs.gameManager?.restart(),
        input: (index, value) => ejs.gameManager?.simulateInput(0, index, value),
        setSpeed: (multiplier) => {
          const gm = ejs.gameManager;
          if (!gm) return;
          if (multiplier > 1) gm.setFastForwardRatio(multiplier);
          gm.toggleFastForward(multiplier > 1 ? 1 : 0);
        },
      });
    };
    const script = document.createElement("script");
    script.src = `${DATA_PATH}loader.js`;
    script.onerror = () => reject(new Error(`Could not load the ${EJS_SYSTEM_LABEL[system]} emulator.`));
    document.body.appendChild(script);
  });
  session.catch(() => {
    session = null;
  });
  return session;
}
